'use client';

import {
  confirmTransaction,
  DEFAULT_LANDING_CONFIG,
  type SigningDeps,
  signTransaction,
  useAuthorizeGiftClaim,
  useFinalizeGiftClaim,
} from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useImpersonationStore } from '@/lib/impersonation';
import { reportError, trackAction } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';

/**
 * Gift claim — new contract dual-signer flow.
 *
 * 1. Call `useAuthorizeGiftClaim` (POST /v1/gifts/claim/authorize) —
 *    backend verifies Turnkey sub-org ownership of both the
 *    destination wallet AND the recipient handle, then returns a
 *    base64 partially-signed VersionedTransaction (backend_authority
 *    already signed; recipient slot still empty).
 * 2. Deserialize, have the recipient wallet add its signature via the
 *    standard SDK `signTransaction` helper.
 * 3. Submit the now-fully-signed tx to the RPC, wait for confirmation.
 *
 * No ALT, no stale-deposit-count check, no relay co-sign — the
 * recipient wallet IS the fee payer on the new flow, and the backend
 * signature is baked into the partial tx returned from `authorize`.
 */

export type ClaimGiftStep =
  | 'idle'
  | 'authorizing'
  | 'signing'
  | 'submitting'
  | 'confirming'
  | 'confirmed'
  | 'failed';

export interface SubmitClaimInput {
  /** Server-issued opaque permalink id for the gift. */
  opaqueId: string;
  /** Wallet that will sign + submit the claim — must match Turnkey sub-org. */
  claimant: { id: string; address: string; type: 'embedded' | 'imported' | 'external' };
  /** Pubkey receiving the assets; equals claimant.address in the standard flow. */
  destinationWalletPubkey: string;
  signingDeps: SigningDeps;
}

export interface ClaimGiftResult {
  signature: string;
  giftPda: string;
  submittedAt: string;
}

export interface UseClaimGiftReturn {
  step: ClaimGiftStep;
  error: string | null;
  result: ClaimGiftResult | null;
  submit: (input: SubmitClaimInput) => Promise<ClaimGiftResult | null>;
  reset: () => void;
}

interface AuthorizeResponse {
  partiallySignedTxBase64: string;
  giftPda: string;
  recipientWallet: string;
  blockhash: string;
  lastValidBlockHeight: number;
  expiresAt: number;
}

const IS_DEV: boolean = process.env.NODE_ENV === 'development';

export function useClaimGift(): UseClaimGiftReturn {
  const impersonationActive = useImpersonationStore((s) => s.isActive);
  const authorizeMutation = useAuthorizeGiftClaim();
  const finalizeMutation = useFinalizeGiftClaim();
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const [step, setStep] = useState<ClaimGiftStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClaimGiftResult | null>(null);
  const t = useTranslations('gift.claim.toast');

  function reset(): void {
    setStep('idle');
    setError(null);
    setResult(null);
  }

  async function submit(input: SubmitClaimInput): Promise<ClaimGiftResult | null> {
    setStep('authorizing');
    setError(null);
    setResult(null);

    showToast({
      id: TOAST_IDS.GIFT_CLAIMING,
      message: t('preparing'),
      variant: 'loading',
      duration: Number.POSITIVE_INFINITY,
    });
    trackAction('gift_claim_started', { walletType: input.claimant.type });

    try {
      // Generated mutation hook reads `vars.opaqueId` and
      // `vars.destinationWalletPubkey` directly (see
      // `useAuthorizeGiftClaim.gen.ts:17`). Previous `{ body: {...} }`
      // wrapper made every field arrive as undefined → 400 from the
      // backend.
      const raw = await authorizeMutation.mutateAsync({
        opaqueId: input.opaqueId,
        destinationWalletPubkey: input.destinationWalletPubkey,
      } as never);
      const auth = ((raw as { data?: AuthorizeResponse }).data ??
        (raw as AuthorizeResponse)) as AuthorizeResponse;

      setStep('signing');
      showToast({
        id: TOAST_IDS.GIFT_CLAIMING,
        message: t('signing'),
        variant: 'loading',
        duration: Number.POSITIVE_INFINITY,
      });

      const partiallySignedBytes = Uint8Array.from(
        Buffer.from(auth.partiallySignedTxBase64, 'base64'),
      );
      // Pass the FULL partial tx (backend authority sig already in its
      // slot, recipient slot empty) to Turnkey. Turnkey's signTransaction
      // expects the full tx bytes — NOT message-only — and returns the
      // fully-signed tx ready to submit.
      const fullySignedBytes = await signTransaction(
        { address: input.claimant.address, type: input.claimant.type },
        partiallySignedBytes,
        input.signingDeps,
        logger,
        impersonationActive,
        IS_DEV,
      );

      setStep('submitting');
      showToast({
        id: TOAST_IDS.GIFT_CLAIMING,
        message: t('submitting'),
        variant: 'loading',
        duration: Number.POSITIVE_INFINITY,
      });
      const connection = getConnection();
      const signature = await connection.sendRawTransaction(fullySignedBytes, {
        skipPreflight: false,
        maxRetries: 3,
      });

      setStep('confirming');
      // Use the blockhash baked into the tx (returned by the backend
      // alongside the partial tx), NOT a freshly-fetched one. A fresh
      // blockhash would track the wrong validity window — the tx
      // expires when the SIGNED blockhash falls out of the recent
      // blockhash queue, not when some unrelated newer blockhash does.
      const confirmation = await confirmTransaction(
        connection,
        signature,
        { blockhash: auth.blockhash, lastValidBlockHeight: auth.lastValidBlockHeight },
        DEFAULT_LANDING_CONFIG,
        `gift-claim:${signature.slice(0, 8)}`,
        logger,
      );

      if (confirmation.status !== 'confirmed') {
        const reason =
          confirmation.status === 'failed'
            ? `Transaction reverted on-chain: ${JSON.stringify(confirmation.error.details?.confirmationError ?? confirmation.error.code)}`
            : confirmation.status === 'expired'
              ? 'Transaction expired before confirming — try again.'
              : confirmation.status === 'timeout'
                ? `Confirmation timed out — RPC may be lagging. Signature: ${signature}`
                : 'Claim was cancelled.';
        setStep('failed');
        setError(reason);
        showToast({
          id: TOAST_IDS.GIFT_CLAIMING,
          message: t('failed'),
          variant: 'error',
        });
        trackAction('gift_claim_failed', { signature, error: reason.slice(0, 200) });
        return null;
      }

      // Tell the backend the claim signature so it flips
      // `gifts.status = 'claimed'` and stamps `claim_tx` + `claimed_at`.
      // Non-fatal — the on-chain claim has already happened; this just
      // keeps the App DB in sync until the indexer catches up.
      try {
        await finalizeMutation.mutateAsync({
          giftPda: auth.giftPda,
          signature,
          claimerWallet: input.claimant.address,
        } as never);
      } catch (finalizeErr) {
        logger.warn('finalizeGiftClaim failed; status will lag until indexer reconciles', {
          giftPda: auth.giftPda,
          err: finalizeErr instanceof Error ? finalizeErr.message : String(finalizeErr),
        });
      }

      const success: ClaimGiftResult = {
        signature,
        giftPda: auth.giftPda,
        submittedAt: new Date().toISOString(),
      };
      setResult(success);
      setStep('confirmed');

      invalidateWalletBalances();

      showToast({
        id: TOAST_IDS.GIFT_CLAIMING,
        message: t('success'),
        variant: 'success',
        duration: 5000,
      });
      trackAction('gift_claim_success', { signature });
      return success;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      reportError(err instanceof Error ? err : new Error(String(err)), {
        component: 'useClaimGift',
      });
      setStep('failed');
      setError(message);
      showToast({
        id: TOAST_IDS.GIFT_CLAIMING,
        message: t('failed'),
        variant: 'error',
      });
      trackAction('gift_claim_failed', { error: message.slice(0, 200) });
      return null;
    }
  }

  return { step, error, result, submit, reset };
}
